import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Row from "@amzn/meridian/esm/components/row";
import Icon from "@amzn/meridian/esm/components/icon";
import Text from "@amzn/meridian/esm/components/text";

import calendarTokens from "@amzn/meridian-tokens/base/icon/calendar";
import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import badgeModerate from "../../../../images/badge_moderate.png";
import badgeDetailed from "../../../../images/badge_detailed.png";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";

import AimsMasthead from "../../AimsMasthead";
import SummaryBodyInDetailPage from "../../../../components/summary/SummaryBodyInDetailPage";
import SummaryFeedback from "../../../../components/feedback/SummaryFeedback";
import SummaryListButton from "../../../../components/summary/SummaryListButton";
import SummaryNotApprovedFeedback from "../../../../components/feedback/SummaryNotApprovedFeedback";
import { SUMMARY_FEEDBACK_STATUS, SUMMARY_TYPES } from "../../../../constants";

import { TABS, VERBOSITY_LEVELS } from "../../../../constants";
import LogManager from "../../../../utils/logManager";
import AttendeeListInDetailPage from "../../../../components/summary/AttendeeListInDetailPage";
import MeetingParticipants from "../../../../components/summary/MeetingParticipants";

/**
 * SummaryDetail Component
 *
 * This component displays the full detail view of a selected meeting summary.
 * It is rendered when a user navigates to the summary detail page from the summary list.
 *
 * Key Features:
 * - Shows a "Summary List" button to return to the main summary tab
 * - Displays meeting title and date/time
 * - Conditionally renders different content based on summary type:
 *   - Special cases (e.g., short meeting, server error, P&C, etc.) are handled inside <SummaryContent />
 *   - If valid, displays narrative summary, decisions, next steps, and a footer note
 * - Includes optional feedback section if no feedback has been submitted
 * - For default-UI users with attendee data: "All attendees" navigates to MeetingParticipants view
 *
 */
const SummaryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const summary = location.state?.summary;
  const isAttendanceReportEnabled = location.state?.isAttendanceReportEnabled;

  const [showParticipants, setShowParticipants] = useState(false);

  const summaryFeedbackStatus = SUMMARY_TYPES.APPROVED.includes(summary?.summaryType) 
    ? SUMMARY_FEEDBACK_STATUS.APPROVED
    : SUMMARY_TYPES.NOT_APPROVED.includes(summary?.summaryType)
    ? SUMMARY_FEEDBACK_STATUS.NOT_APPROVED
    : SUMMARY_FEEDBACK_STATUS.OTHER;

  const handleTabChange = (tabKey) => {
    navigate("/", { state: { selectedTab: tabKey } });
  };

  const onSummaryList = () => {
    navigate("/", { state: { selectedTab: TABS.SUMMARIES } });
  };

  if (!summary) {
    LogManager.error("No summary in SummaryDetail view. This should not happen.");
    return (
      <Column>
        <Text type="b200" color="error">
          Summary not found.
        </Text>
      </Column>
    );
  }

  // Show Meeting Participants view
  if (showParticipants) {
    return (
      <Column spacing="none" heights={["fill", "fit", "fill"]}>
        <Column spacing="none">
          <AimsMasthead selectedTab={TABS.SUMMARIES} setSelectedTab={handleTabChange} />
          <Column height="40px" />
        </Column>

        <Row widths={["32px", "326px", "fill"]} spacing="none" spacingInset="none">
          <Row width="32px" />
          <Box width="326px" height="calc(100vh - 130px)" spacing="none" spacingInset="none" overflowY="scroll">
            <Column spacing="none" spacingInset="none" width="306px">
              <MeetingParticipants
                attendees={summary.attendees}
                onBack={() => setShowParticipants(false)}
              />
            </Column>
          </Box>
        </Row>
      </Column>
    );
  }

  return (
    <Column spacing="none" heights={["fill", "fit", "fill"]}>
      <Column spacing="none">
        {/* AIMS top masthead bar */}
        <AimsMasthead selectedTab={TABS.SUMMARIES} setSelectedTab={handleTabChange} />
        <Column height="40px" />

        {/* "Summary list" link button */}
        <SummaryListButton onSummaryListInClient={onSummaryList} />

        <Column height="24px" />
      </Column>

      <Row widths={["32px", "326px", "fill"]} spacing="none" spacingInset="none">
        <Row width="32px" />

        {/*Scroll bar section*/}
        <Box width="326px" height="calc(100vh - 130px)" spacing="none" spacingInset="none" overflowY="scroll">
          <Column spacing="none" spacingInset="none" width="306px">
            {/* Summary title */}
            <Row spacing="300" alignmentVertical="center">
              <Text type="h200">
                {summary.meetingTitle}
              </Text>
            </Row>
            <Column height="20px" />

            {/* Summary time with calendar icon */}
            <Row spacing="none" spacingInset="none">
              <Icon tokens={calendarTokens} color="secondary" />
              <Row width="4px" />
              <Text type="b100" color="secondary" className="summary-time">
                {summary.meetingDateAndTime}
              </Text>
            </Row>

            <Column height="8px" />

            {/* Attendee list */}
            <AttendeeListInDetailPage
              attendeeNameList={summary.attendeeNameList}
              attendees={summary.attendees}
              isAttendanceReportEnabled={isAttendanceReportEnabled}
              onViewParticipants={() => setShowParticipants(true)}
            />

            {/*Divider*/}
            <Column height="16px" />
            <Divider spacingBefore="0" spacingAfter="0" />
            <Column height="16px" />

            {summary.summaryDetailLevel && summary.summaryDetailLevel !== VERBOSITY_LEVELS.CONCISE && (
              <>
                <img
                  src={summary.summaryDetailLevel === VERBOSITY_LEVELS.MODERATE ? badgeModerate : badgeDetailed}
                  alt={summary.summaryDetailLevel}
                  width={90}
                  height={20}
                />
                <Column height="16px" />
              </>
            )}

            <SummaryBodyInDetailPage summary={summary} isTopicNotApproved={summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED}/>
          </Column>
        </Box>
      </Row>

      {/* Feedback */}
      {(!summary.feedback && !summary.feedbackComment) && (
        <Box width="358px">
          {summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.APPROVED ? (
            <SummaryFeedback aimsUuid={id} />
          ) : summaryFeedbackStatus === SUMMARY_FEEDBACK_STATUS.NOT_APPROVED ? (
            <SummaryNotApprovedFeedback aimsUuid={id} />
          ) : null
          }
        </Box>
      )}
    </Column>
  );
};

export default SummaryDetail;
